import { z } from 'zod';

import { ExperienceReportRequestSchema, ExperienceReportWithHeaderSchema } from './experience-report';
import { FinancialReportV2RequestSchema, FinancialReportV2WithHeaderSchema } from './financial-report-v2';
import { MarketMapReportRequestSchema, MarketMapReportWithHeaderSchema } from './market-map-report';
import { BrandReportHeaderSchema, BrandStatus } from '../brand/report';
import { makeEndpoint } from '../common';
import { FinancialReportRequestSchema, FinancialReportWithHeaderSchema } from '../report/financial-report';
import { GuestReportRequestSchema, GuestReportWithHeaderSchema } from '../report/guest-report';

export const NO_DATA_TO_DOWNLOAD_ERROR_MESSAGE = 'No data to download';

export enum ReportMode {
  PREVIEW = 'preview',
  DOWNLOAD = 'download',
  WEBHOOK = 'webhook',
}

export enum ReportStatus {
  PENDING = 'pending',
  SUCCESS = 'success',
  FAILED = 'failed',
}

export const ReportErrorsSchema = z.object({
  message: z.string(),
});
export type ReportErrors = z.infer<typeof ReportErrorsSchema>;

export enum ReportFormat {
  CSV = 'csv',
  PDF = 'pdf',
}

export enum ReportDateRange {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  LAST_7_DAYS = 'last-7-days',
  LAST_14_DAYS = 'last-14-days',
  LAST_30_DAYS = 'last-30-days',
  THIS_MONTH = 'this-month',
  LAST_MONTH = 'last-month',
  TOMORROW = 'tomorrow',
  NEXT_3_DAYS = 'next-3-days',
  NEXT_7_DAYS = 'next-7-days',
  CUSTOM = 'custom',
}

export const ReportDateRangeLabel: Record<ReportDateRange, string> = {
  [ReportDateRange.TODAY]: 'Today',
  [ReportDateRange.YESTERDAY]: 'Yesterday',
  [ReportDateRange.LAST_7_DAYS]: 'Last 7 days',
  [ReportDateRange.LAST_14_DAYS]: 'Last 14 days',
  [ReportDateRange.LAST_30_DAYS]: 'Last 30 days',
  [ReportDateRange.THIS_MONTH]: 'This month',
  [ReportDateRange.LAST_MONTH]: 'Last month',
  [ReportDateRange.TOMORROW]: 'Tomorrow',
  [ReportDateRange.NEXT_3_DAYS]: 'Next 3 days',
  [ReportDateRange.NEXT_7_DAYS]: 'Next 7 days',
  [ReportDateRange.CUSTOM]: 'Custom',
};

export enum ReportType {
  BRANDS = 'brands',
  FINANCIAL = 'financial',
  FINANCIAL_V2 = 'financial-v2',
  EXPERIENCE = 'experience',
  GUEST = 'guest',
  MARKET_MAP = 'market-map',
}

export const ReportTypeName: Record<ReportType, string> = {
  [ReportType.BRANDS]: 'brands',
  [ReportType.GUEST]: 'guest',
  [ReportType.EXPERIENCE]: 'experience',
  [ReportType.FINANCIAL]: 'financial',
  [ReportType.FINANCIAL_V2]: 'financial',
  [ReportType.MARKET_MAP]: 'market map',
};

const capitalize = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

export const ReportTypeLabel: Record<ReportType, string> = {
  [ReportType.BRANDS]: `${capitalize(ReportTypeName[ReportType.BRANDS])} Report`,
  [ReportType.GUEST]: `${capitalize(ReportTypeName[ReportType.GUEST])} Report`,
  [ReportType.EXPERIENCE]: `${capitalize(ReportTypeName[ReportType.EXPERIENCE])} Report`,
  [ReportType.FINANCIAL]: `${capitalize(ReportTypeName[ReportType.FINANCIAL])} Report`,
  [ReportType.FINANCIAL_V2]: `${capitalize(ReportTypeName[ReportType.FINANCIAL_V2])} Report`,
  [ReportType.MARKET_MAP]: 'Market Map Report',
};

export const brandReportInfoSchema = {
  filters: {
    brandStatuses: Object.values(BrandStatus),
  },
  sortBy: [],
  columns: [],
} as const;

export const BrandReportRequestSchema = z.object({
  params: z.object({
    brandStatuses: z.array(z.nativeEnum(BrandStatus)),
  }),
  format: z.enum([ReportFormat.CSV, ReportFormat.PDF]).optional(),
});

export const ReportRequestSchema = z.union([BrandReportRequestSchema, FinancialReportRequestSchema]);
export type ReportRequest = z.infer<typeof ReportRequestSchema>;

export const ReportSchemaByType = {
  [ReportType.BRANDS]: BrandReportRequestSchema,
  [ReportType.FINANCIAL]: FinancialReportRequestSchema,
  [ReportType.FINANCIAL_V2]: FinancialReportV2RequestSchema,
  [ReportType.EXPERIENCE]: ExperienceReportRequestSchema,
  [ReportType.GUEST]: GuestReportRequestSchema,
  [ReportType.MARKET_MAP]: MarketMapReportRequestSchema,
} satisfies Record<ReportType, z.ZodType>;

export const FindReportResponseSchema = z.object({
  id: z.string(),
  payload: z.record(z.string(), z.unknown()),
  type: z.nativeEnum(ReportType),
  status: z.nativeEnum(ReportStatus),
  errors: ReportErrorsSchema.nullable(),
  completionPercentage: z.number().nullable(),
  createdById: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  media: z
    .object({
      id: z.string(),
      originalName: z.string(),
      uri: z.object({
        original: z.string(),
      }),
    })
    .nullable(),
});

export const GenerateReportRequestSchema = z.union([
  BrandReportRequestSchema,
  ExperienceReportRequestSchema,
  FinancialReportRequestSchema,
  GuestReportRequestSchema,
  FinancialReportV2RequestSchema,
  MarketMapReportRequestSchema,
]);

export type FindReportResponse = z.infer<typeof FindReportResponseSchema>;

export const ReportDataByTypeSchema = {
  [ReportType.BRANDS]: BrandReportHeaderSchema,
  [ReportType.FINANCIAL]: z.union([FinancialReportWithHeaderSchema, z.record(z.string(), z.unknown())]),
  [ReportType.FINANCIAL_V2]: z.union([FinancialReportV2WithHeaderSchema, z.record(z.string(), z.unknown())]),
  [ReportType.EXPERIENCE]: z.union([ExperienceReportWithHeaderSchema, z.record(z.string(), z.unknown())]),
  [ReportType.GUEST]: z.union([GuestReportWithHeaderSchema, z.record(z.string(), z.unknown())]),
  [ReportType.MARKET_MAP]: z.union([MarketMapReportWithHeaderSchema, z.record(z.string(), z.unknown())]),
} satisfies Record<ReportType, z.ZodType>;

export const PostReportEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ type }: { type: ReportType }) => `v2/reports/${type}`,
  responseSchema: FindReportResponseSchema,
  requestSchema: GenerateReportRequestSchema,
});

export const GetReportEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ reportId }: { reportId: string }) => `v2/reports/${reportId}`,
  responseSchema: FindReportResponseSchema,
  requestSchema: z.void(),
});

const infoItemSchema = z.object({
  fieldType: z.enum(['text', 'single-select', 'multi-select', 'checkbox', 'date']),
  label: z.string(),
  key: z.string(),
  values: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
});

export type ReportInfoItem = z.infer<typeof infoItemSchema>;

const reportInfoResponseSchema = z.object({
  filters: z.array(infoItemSchema),
  experiences: z.array(
    z.object({
      /** Archived ("deleted") experiences and collections keep their bookings, so reports can still filter by them. */
      isArchived: z.boolean(),
      label: z.string(),
      value: z.string(),
      kind: z.enum(['experience', 'collection']),
    }),
  ),
});

export type ReportInfoResponse = z.infer<typeof reportInfoResponseSchema>;

export const GetReportInfoEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ reportType }: { reportType: ReportType }) => `v2/reports/info/${reportType}`,
  responseSchema: reportInfoResponseSchema,
  requestSchema: z.void(),
});

export const DownloadReportEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ reportId }: { reportId: string }) => `v2/reports/${reportId}/download`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});
